import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { OptimizeRequest, RoutesMonitoringResponse, Run, Task, WayPoint } from '@bringg/types';
import { getRoot } from 'mobx-easy';
import {
	EtaCalculationUpdateResults,
	EtaCalculator,
	EtaResponse,
	FullEtaRequest
} from '@bringg-frontend/eta-calculator';

import RootStore from 'bringg-web/stores/root-store';
import {
	didTaskDataChanged,
	getRouteData,
	getRouteTasks,
	mapTaskOriginData,
	getOptimizationTypeByRouteTeam
} from './helpers';

let etaCalculator: EtaCalculator;

type OriginalTaskData = Pick<Task, 'id' | 'priority' | 'status'> & Pick<WayPoint, 'lat' | 'lng'>;

type RoutesMonitoringCalculationResults = EtaCalculationUpdateResults & {
	originalTasksData: OriginalTaskData[];
};

const routesCalculations: Map<number, RoutesMonitoringCalculationResults[]> = new Map();

const failedCalculations: Map<number, boolean> = new Map();

const recalculateEtaFetch = async (etaRequest: FullEtaRequest): Promise<EtaResponse> =>
	getRootEnv().dashboardSdk.sdk.application.executeAction('calculate_planned_eta', etaRequest, {
		timeout: 100000
	});

const calculatePlannedEta = async (etaRequest: OptimizeRequest) => {
	return getRootEnv().dashboardSdk.sdk.routeOptimizationApi.calculatePlannedEta({ ...etaRequest, apply: true });
};

const init = async (): Promise<EtaCalculator> => {
	if (etaCalculator) return etaCalculator;
	await getRoot<RootStore>().data.merchantConfigurationsStore.fetch();

	etaCalculator = new EtaCalculator(
		// eslint-disable-next-line @typescript-eslint/unbound-method
		recalculateEtaFetch,
		calculatePlannedEta,
		getRoot<RootStore>().data.usersStore.currentUser
	);

	return etaCalculator;
};

const updateRoutesCalculationAfterEta = (
	ids: number[],
	data: PromiseSettledResult<EtaCalculationUpdateResults[]>[]
) => {
	ids.forEach((routeId, index) => {
		if (data[index]?.status === 'fulfilled') {
			const etaData = (data[index] as PromiseFulfilledResult<RoutesMonitoringCalculationResults[]>).value;

			const route = getRoot<RootStore>().data.runStore.get(Number(routeId));
			etaData[0].originalTasksData = getRouteTasks(route).map(mapTaskOriginData);

			routesCalculations.set(routeId, etaData);
		} else {
			failedCalculations.set(routeId, true);
			console.error(`failed to get eta for route ${routeId}: `, (data[index] as PromiseRejectedResult).reason);
		}
	});
};

const getEtaPromises = (routes: Run[], etaFunction: any, routeId: number) => {
	let response;

	try {
		const routeData = routes.find(run => run.id === Number(routeId));
		const tasks = getRouteTasks(routeData);
		const optimizationType = getOptimizationTypeByRouteTeam(routeData.team_id);

		response = etaFunction(
			[getRouteData(routeData, tasks)],
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			() => {},
			optimizationType,
			routeData.team_id,
			true
		);
		return response;
	} catch (e) {
		routesCalculations.set(routeId, null);
		return response;
	}
};

const getEtaCalculations = async (ids: number[]) => {
	const routes = await getRoot<RootStore>().data.runStore.loadMany({ ids });
	const etaPromises: Promise<EtaCalculationUpdateResults[]>[] = [];

	ids.forEach(routeId => {
		if (routesCalculations.get(routeId)) {
			etaPromises.push(Promise.resolve(routesCalculations.get(routeId)));
		} else {
			const response = getEtaPromises(routes, etaCalculator.runEtaCalculations.bind(etaCalculator), routeId);
			etaPromises.push(response);
		}
	});

	const data = await Promise.allSettled(etaPromises);
	failedCalculations.clear();
	updateRoutesCalculationAfterEta(ids, data);

	return data;
};

const getCalculatePlannedEta = async (ids: number[]) => {
	const routes = await getRoot<RootStore>().data.runStore.loadMany({ ids });
	const etaPromises: Promise<EtaCalculationUpdateResults[]>[] = [];

	ids.forEach(routeId => {
		const response = getEtaPromises(routes, etaCalculator.runCalculatePlannedEta.bind(etaCalculator), routeId);
		etaPromises.push(response);
	});

	const data = await Promise.allSettled(etaPromises);
	failedCalculations.clear();

	return data;
};

const resetRouteCalculation = (routeId: number, shouldDelete?: boolean): any => {
	if (shouldDelete) {
		routesCalculations.delete(routeId);
	} else {
		routesCalculations.set(routeId, null);
	}
};

const shouldRecalculateDirection = (route: RoutesMonitoringResponse): boolean => {
	const calculation = routesCalculations.get(route.id);
	if (!calculation) return true;
	const { originalTasksData } = calculation[0];

	if (originalTasksData.length !== route.task_ids.length) {
		return true;
	}

	return originalTasksData.some(task => didTaskDataChanged(task));
};

const isFailed = (routeId: number): boolean => failedCalculations.get(routeId);

const clear = () => {
	routesCalculations.clear();
	failedCalculations.clear();
};

export const etaCalculatorProvider = {
	init,
	getEtaCalculations,
	resetRouteCalculation,
	shouldRecalculateDirection,
	isFailed,
	clear,
	getCalculatePlannedEta
};
